import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/utils";
import { BillingMetrics } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { CreditCard, DollarSign, LucideIcon, TrendingUp, Users } from "lucide-react";

function MetricCard({
  title,
  value,
  icon: Icon,
  change,
}: {
  title: string;
  value: string;
  icon: LucideIcon;
  change?: string;
}) {
  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{value}</p>
          {change && (
            <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
              <TrendingUp className="h-4 w-4" />
              {change}
            </p>
          )}
        </div>
        <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-3">
          <Icon className="h-6 w-6 text-green-600" />
        </div>
      </div>
    </div>
  );
}

export function BillingPage() {
  const { data: metrics, isLoading } = useQuery<BillingMetrics>({
    queryKey: ["billing-metrics"],
    queryFn: api.getBillingMetrics,
    refetchInterval: 60000, // Refetch every minute
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 dark:border-zinc-600 border-t-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-zinc-400">Loading billing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-0">
      <PageHeader title="Billing & Revenue" subtitle="Financial metrics and subscription management" />

      {/* Metrics Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8">
        <MetricCard
          title="Monthly Recurring Revenue"
          value={formatCurrency(metrics?.mrr || 0)}
          icon={DollarSign}
          change="+15% from last month"
        />
        <MetricCard
          title="Annual Recurring Revenue"
          value={formatCurrency(metrics?.arr || 0)}
          icon={TrendingUp}
          change="+18% from last year"
        />
        <MetricCard
          title="Total Revenue"
          value={formatCurrency(metrics?.totalRevenue || 0)}
          icon={DollarSign}
        />
        <MetricCard
          title="Active Subscriptions"
          value={metrics?.activeSubscriptions?.toString() || "0"}
          icon={CreditCard}
          change="+12% from last month"
        />
        <MetricCard
          title="Average Revenue Per User"
          value={formatCurrency(metrics?.averageRevenuePerUser || 0)}
          icon={Users}
        />
        <MetricCard
          title="Churn Rate"
          value={`${(metrics?.churnRate || 0).toFixed(2)}%`}
          icon={TrendingUp}
        />
      </div>

      {/* Revenue Chart Placeholder */}
      <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Revenue Trend</h2>
        <div className="h-64 flex items-center justify-center bg-gray-50 dark:bg-zinc-700 rounded-lg">
          <p className="text-gray-500 dark:text-zinc-400">Revenue chart will be displayed here</p>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Transactions</h2>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-zinc-700 last:border-0"
            >
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Organization Name {i}</p>
                <p className="text-sm text-gray-500 dark:text-zinc-400">Pro Plan - Monthly</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(99)}</p>
                <p className="text-sm text-gray-500 dark:text-zinc-400">2 hours ago</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
